"use client";

import { useState, useEffect } from "react";
import { Plus, Trash2, Edit, Eye, Loader2, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { courseAPI } from "../lib/api";

/* ✅ TYPES - MATCHING PRISMA MODEL */
interface Course {
  id: string;
  name: string;
  title: string;
  subtitle: string | null;
  coverImage: string | null;
  objectives: string[];
  timeline: string | null;
  startDate: string | null;
  status: "PUBLISHED" | "DRAFT";
  sections: any[];
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
}

/* ✅ PROPS */
interface CoursesSectionProps {
  onCreate: () => void;
  onEdit: (course: Course) => void;
}

export function CoursesSection({ onCreate, onEdit }: CoursesSectionProps) {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
   
  const fetchCourses = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await courseAPI.getAll();
      console.log("✅ API RESPONSE:", response);

      if (!response) return;

      let coursesData = Array.isArray(response)
        ? response
        : response.data || response.courses || [];

      coursesData = coursesData.map((course: any) => ({
        ...course,
        subtitle: course.subtitle || '',
        timeline: course.timeline || '',
        startDate: course.startDate || null,
      }));

      setCourses(coursesData);
    } catch (err: any) {
      console.error("Error fetching courses:", err);
      setError(err.message || "Failed to fetch courses");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  const handleDeleteCourse = async (courseId: string) => {
    if (!window.confirm("Are you sure you want to delete this course?")) {
      return;
    }

    try {
      setDeleting(courseId);
      await courseAPI.delete(courseId);
      setCourses(courses.filter((c) => c.id !== courseId));
    } catch (err: any) {
      alert(err.message || "Failed to delete course");
      console.error("Error deleting course:", err);
    } finally {
      setDeleting(null);
    }
  };

  const getCourseStatus = (course: Course): string => {
    const now = new Date();
    const startDate = course.startDate ? new Date(course.startDate) : null;
    const isNotStarted = startDate && now < startDate;
    
    if (isNotStarted) return "Not Started";
    if (course.status === "DRAFT") return "draft";
    return "Draft";
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Published":
        return "bg-green-100 text-green-700";
      case "Draft":
        return "bg-yellow-100 text-yellow-700";
      case "Not Started":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">Loading courses...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">My Courses</h2>
            <p className="text-gray-500 mt-1">
              Manage your courses and content
            </p>
          </div>
          <Button
            onClick={onCreate}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Plus className="w-4 h-4 mr-2" /> Create New Course
          </Button>
        </div>
        <Card className="p-12">
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <Button onClick={fetchCourses}>Try Again</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">My Courses</h2>
          <p className="text-gray-500 mt-1">
            Manage your courses and content
          </p>
        </div>

        <Button
          onClick={onCreate}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" /> Create New Course
        </Button>
      </div>

      {courses.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <p className="text-gray-500 mb-4">
              No courses yet. Create your first course to get started!
            </p>
            <Button onClick={onCreate}>
              <Plus className="w-4 h-4 mr-2" /> Create Course
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => {
            const courseStatus = getCourseStatus(course);
            const startDate = course.startDate ? new Date(course.startDate) : null;
            const isNotStarted = startDate && new Date() < startDate;
            
            return (
              <Card
                key={course.id}
                className="overflow-hidden hover:shadow-lg transition-shadow"
              >
                <CardHeader className="p-0">
                  {course.coverImage ? (
                    <img
                      src={course.coverImage}
                      alt={course.name}
                      className="w-full h-48 object-cover"
                    />
                  ) : (
                    <div className="w-full h-48 bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <p className="text-white text-lg font-semibold">
                        {course.name?.charAt(0) || 'C'}
                      </p>
                    </div>
                  )}
                </CardHeader>

                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg line-clamp-1">
                        {course.name}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-1">
                        {course.subtitle || 'No subtitle'}
                      </p>
                    </div>

                    <Badge className={getStatusColor(courseStatus)}>
                      {courseStatus}
                    </Badge>
                  </div>

                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Sections:</span>
                      <span>{course.sections?.length || 0}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="font-medium">Chapters:</span>
                      <span>
                        {course.sections?.reduce(
                          (acc, s) => acc + (s.chapters?.length || 0),
                          0
                        ) || 0}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="font-medium">Duration:</span>
                      <span>{course.timeline || "N/A"}</span>
                    </div>

                    {startDate && (
                      <div className={`flex items-center gap-2 ${isNotStarted ? 'text-blue-600' : 'text-green-600'}`}>
                        <Calendar className="w-4 h-4" />
                        <span>
                          {isNotStarted 
                            ? `Starts: ${startDate.toLocaleDateString()}`
                            : `Started: ${startDate.toLocaleDateString()}`}
                        </span>
                      </div>
                    )}

                    {course.publishedAt && course.status === "PUBLISHED" && (
                      <div className="flex items-center gap-2 text-gray-500 text-xs">
                        <span>Published: {new Date(course.publishedAt).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </CardContent>

                <CardFooter className="p-4 pt-0 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => onEdit(course)}
                  >
                    <Edit className="w-4 h-4 mr-2" /> Edit
                  </Button>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteCourse(course.id)}
                    disabled={deleting === course.id}
                  >
                    {deleting === course.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 text-red-500" />
                    )}
                  </Button>

                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => window.open(`/course/${course.id}`, '_blank')}
                  >
                    <Eye className="w-4 h-4" />
                  </Button>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}